/* @flow */

import * as React from 'react';

import type { NETGEM_API_V8_METADATA_SCHEDULE } from '../../../libs/netgemLibrary/v8/types/MetadataSchedule';

import AccurateTimestamp from '../../../helpers/dateTime/AccurateTimestamp';
import { getIso8601DateInSeconds, getIso8601DurationInSeconds } from '../../../helpers/dateTime/Format';
import Localizer from '../../../helpers/localization';
import { BroadcastStatus, WebAppHelpersLocationStatus } from '../../../helpers/ui/location/Format';
import { getLocationType } from '../../../libs/netgemLibrary/v8/helpers/Item';
import { ItemContent, ItemLocationKind, ItemType, type NETGEM_API_V8_FEED_ITEM, type NetgemApiV8ItemLocation } from '../../../libs/netgemLibrary/v8/types/FeedItem';
import {
  type ID_TYPE,
  type NETGEM_API_V8_SCHEDULED_RECORDING,
  type NETGEM_RECORDINGS_MAP,
  type NETGEM_SCHEDULED_RECORDINGS_MAP,
  RecordingOutcome,
  ScheduledRecordingsKind,
  type ScheduledRecordingsKindType,
} from '../../../libs/netgemLibrary/v8/types/Npvr';
import { PictoClock, PictoRecord } from '../../pictos/Element';
import WatchingStatus from '../../watchingStatus/WatchingStatus';

const getRecordingOutcomes = (
  npvrRecordingsList: NETGEM_RECORDINGS_MAP,
  npvrRecordingsFuture: NETGEM_RECORDINGS_MAP,
  seriesScheduledRecording: ?NETGEM_API_V8_SCHEDULED_RECORDING,
  singleScheduledRecording: ?NETGEM_API_V8_SCHEDULED_RECORDING,
  item: NETGEM_API_V8_FEED_ITEM,
  scheduledEventId: string,
  tvLocationMetadata: NETGEM_API_V8_METADATA_SCHEDULE | null,
): Array<RecordingOutcome> => {
  const {
    selectedLocation: { duration, startDate },
    selectedProgramId: programId,
  } = item;

  let associatedRecords: Array<ID_TYPE> | null = null;
  if (singleScheduledRecording) {
    ({ records: associatedRecords } = singleScheduledRecording);
  } else if (seriesScheduledRecording) {
    ({ records: associatedRecords } = seriesScheduledRecording);
  }

  if (!associatedRecords) {
    return [];
  }

  const outcomes: Array<RecordingOutcome> = [];

  // Search in existing recordings
  const { [programId]: recordings } = npvrRecordingsList;
  if (recordings) {
    // Check first if program is live right now
    const now = AccurateTimestamp.nowInSeconds();
    const start = getIso8601DateInSeconds(startDate);
    const end = start + getIso8601DurationInSeconds(duration);
    const startMargin = getIso8601DurationInSeconds(tvLocationMetadata?.location.startMargin);
    const isLive = start - startMargin <= now && now < end;

    associatedRecords.forEach((r) => {
      const recording = recordings.find((rec) => rec.id === r.id);
      if (recording?.recordOutcome && (!isLive || recording.scheduledEventStartDate === startDate)) {
        outcomes.push(recording.recordOutcome);
      }
    });
  }

  // Search in future recordings
  const { [programId]: futureRecordings } = npvrRecordingsFuture;
  if (futureRecordings) {
    const recording = futureRecordings.find((rec) => rec.id === scheduledEventId);
    if (recording) {
      const { matchingScheduledRecordings } = recording;
      if (matchingScheduledRecordings) {
        matchingScheduledRecordings.forEach((sr) => outcomes.push(sr.recordOutcome));
      }
    }
  }

  return outcomes;
};

const getSingleScheduledRecording = (
  npvrScheduledRecordingsList: NETGEM_SCHEDULED_RECORDINGS_MAP,
  npvrRecordingsList: NETGEM_RECORDINGS_MAP,
  hasSeriesScheduledRecording: boolean,
  broadcastStatus: BroadcastStatus,
  selectedProgramId: string,
  localScheduledEventId: string,
): ?NETGEM_API_V8_SCHEDULED_RECORDING => {
  /*
   * In case of a live item, a single scheduled recording should override any series scheduled recording
   * Special case where user started a single recording on a live item, then started a series recording
   */
  if (broadcastStatus !== BroadcastStatus.Past && (!hasSeriesScheduledRecording || broadcastStatus === BroadcastStatus.Live)) {
    return npvrScheduledRecordingsList[localScheduledEventId];
  }

  // Keep from replay special case: look for program Id in recording list
  const recording = npvrRecordingsList[selectedProgramId]?.find(({ recordOutcome: ro }) => ro === RecordingOutcome.Recorded);
  if (recording) {
    // Then find recording Id in scheduled recording list
    const matchingScheduledRecording = Object.entries(npvrScheduledRecordingsList).find(
      ([key, value]) =>
        // Scheduled recording has to be a scheduled event
        !key.startsWith('series://') &&
        getLocationType(key) === ItemLocationKind.ScheduledEvent &&
        // Scheduled recording kind has to be "keep from replay"
        value.description.kind === ScheduledRecordingsKind.KeepFromReplay &&
        // And recording Id has to match
        value.records.some(({ id }) => id === recording.id),
    );

    if (matchingScheduledRecording) {
      return matchingScheduledRecording[1];
    }
  }

  return undefined;
};

const renderLiveRecordingIcon = (locType: ?NetgemApiV8ItemLocation, broadcastStatus: BroadcastStatus, isRecordOutcomeOK: boolean): React.Node => {
  if (broadcastStatus !== BroadcastStatus.Live || (locType !== (WebAppHelpersLocationStatus.Recording as string) && !isRecordOutcomeOK)) {
    return null;
  }

  return <PictoRecord aria-label={Localizer.localize('accessibility.navigation.item_live_recording')} className='recording live' hasBackground />;
};

const renderRecordingIcon = (locType: ?NetgemApiV8ItemLocation, recordOutcome?: RecordingOutcome): React.Node => {
  if ((locType !== (WebAppHelpersLocationStatus.Recording as string) && locType !== (WebAppHelpersLocationStatus.Catchup as string)) || recordOutcome !== RecordingOutcome.Recorded) {
    // Not a recording, not a catchup (keep from replay), or record outcome is not OK
    return null;
  }

  return <PictoRecord aria-label={Localizer.localize('accessibility.navigation.item_recording')} className='recording' hasBackground />;
};

const renderScheduledRecordingIcon = (broadcastStatus: BroadcastStatus, hasSeriesScheduledRecording: boolean, hasSingleScheduledRecording: boolean): React.Node => {
  if (broadcastStatus === BroadcastStatus.Live || broadcastStatus === BroadcastStatus.Past || (!hasSeriesScheduledRecording && !hasSingleScheduledRecording)) {
    return null;
  }

  return <PictoClock aria-label={Localizer.localize('accessibility.navigation.item_scheduled_recording')} className='scheduledRecording' hasBackground />;
};

const renderDebugInfoElement = (
  item: NETGEM_API_V8_FEED_ITEM,
  hasSingleScheduledRecording: boolean,
  hasSeriesScheduledRecording: boolean,
  recordingOutcomes: Array<RecordingOutcome>,
  channelNumber: number,
  isDebugModePlusEnabled: boolean,
): React.Node => {
  const {
    id,
    locType,
    score,
    selectedLocation: { matchingScheduledRecordings, recordOutcome },
    seriesId,
  } = item;

  if (!isDebugModePlusEnabled) {
    return null;
  }

  let scheduledRecordingType: ?ScheduledRecordingsKindType = null;
  if (hasSingleScheduledRecording) {
    scheduledRecordingType = ScheduledRecordingsKind.Single;
  } else if (hasSeriesScheduledRecording) {
    scheduledRecordingType = ScheduledRecordingsKind.Series;
  }
  const scheduledRecordingTypeElement = scheduledRecordingType ? <div className='scheduledRecordingType'>Recording kind: {scheduledRecordingType}</div> : null;

  const outcomeToString = (outcome?: RecordingOutcome): string => (outcome === undefined ? 'No record outcome' : (outcome as string));

  let debugContentElement = null;
  if (locType === ItemLocationKind.Recording) {
    // Recording
    debugContentElement = <div className='recordOutcome'>Outcome: {outcomeToString(recordOutcome)}</div>;
  } else if (locType === ItemLocationKind.ScheduledEvent) {
    // Scheduled recording
    let outcomes: Array<RecordingOutcome> = [];
    if (recordingOutcomes.length > 0) {
      outcomes = recordingOutcomes;
    } else if (matchingScheduledRecordings) {
      outcomes = matchingScheduledRecordings.map((sr) => sr.recordOutcome);
    }

    if (outcomes.length > 0) {
      let i = -1;
      debugContentElement = outcomes.map((outcome) => {
        i += 1;

        return (
          <div className='recordOutcome' key={i}>
            Outcome: {outcomeToString(outcome)}
          </div>
        );
      });
    }
  }

  return (
    <div aria-hidden className='debugInfo'>
      <div>
        <span>LCN: {channelNumber}</span>
        <span>{seriesId}</span>
        <span>{id !== seriesId ? id : null}</span>
        <span>{locType}</span>
        <span>{score ? JSON.stringify(score).slice(1, -1) : null}</span>
      </div>
      {scheduledRecordingTypeElement || debugContentElement ? (
        <div className='record'>
          {scheduledRecordingTypeElement}
          {debugContentElement}
        </div>
      ) : null}
    </div>
  );
};

const renderChannelImage = (channelImageUrl: string | null, channelName: string): React.Node => {
  if (!channelImageUrl) {
    return null;
  }

  return <img alt={Localizer.localize('accessibility.navigation.channel_logo', { channelName })} aria-hidden className='channelImage' src={channelImageUrl} />;
};

const renderWatchingStatus = (itemType: ?ItemType, contentType: ItemContent, vodLocationsMetadata: Array<NETGEM_API_V8_METADATA_SCHEDULE> | null, progress: number | null): React.Node => {
  if (contentType === ItemContent.VODOrDeeplink && !vodLocationsMetadata && itemType !== ItemType.Series) {
    return null;
  }

  if (progress === null) {
    return null;
  }

  return <WatchingStatus progress={progress} />;
};

export {
  getRecordingOutcomes,
  getSingleScheduledRecording,
  renderChannelImage,
  renderDebugInfoElement,
  renderLiveRecordingIcon,
  renderRecordingIcon,
  renderScheduledRecordingIcon,
  renderWatchingStatus,
};
